<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4">
    <div class="text-xs font-semibold text-gray-600 mb-1">{{ label }}</div>
    <div class="text-xs text-gray-400 mb-2 leading-snug">{{ description }}</div>

    <!-- own spinner -->
    <div v-if="loading" class="flex items-center py-1">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-dark"></div>
    </div>

    <!-- own error, own retry -->
    <div v-else-if="error" class="text-xs text-red-500">
      Couldn't load.
      <button type="button" class="underline hover:no-underline" @click="fetchData">Retry</button>
    </div>

    <!-- answer -->
    <template v-else>
      <div class="text-sm font-medium text-gray-800">
        <span v-if="mostRecentDate">{{ formatDate(mostRecentDate) }}</span>
        <span v-else class="text-yellow-500">No data</span>
      </div>
      <div class="mt-1 text-xs font-medium" :class="freshnessClass(mostRecentDate)">
        {{ freshnessLabel(mostRecentDate) }}
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * One data-type freshness question, answered independently.
 * Mounts, fires its OWN /api/data-freshness/?node=<key> request, and owns its
 * own loading/error/retry. A slow node (e.g. ADT) only spins this card.
 */
export default {
  name: 'FreshnessCard',
  props: {
    nodeKey: { type: String, required: true },
    label: { type: String, required: true },
    description: { type: String, default: '' },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      error: false,
      mostRecentDate: null,
    };
  },
  watch: {
    entityId() { this.fetchData(); },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = false;
      try {
        const params = { node: this.nodeKey };
        if (this.entityId) params.entity_id = this.entityId;
        const resp = await axios.get('api/data-freshness/', { params });
        this.mostRecentDate = resp.data?.most_recent_date ?? null;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    freshnessClass(date) {
      if (!date) return 'text-gray-400';
      const days = Math.floor((new Date() - new Date(date + 'T00:00:00')) / 86400000);
      if (days <= 7) return 'text-green-600';
      if (days <= 21) return 'text-yellow-600';
      return 'text-red-500';
    },
    freshnessLabel(date) {
      if (!date) return 'No data';
      const days = Math.floor((new Date() - new Date(date + 'T00:00:00')) / 86400000);
      if (days === 0) return 'Today';
      if (days === 1) return '1 day ago';
      return `${days} days ago`;
    },
  },
};
</script>
